/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent} from 'react';
import RadioGroup from '@material-ui/core/RadioGroup';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {get} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {getMinDate} from '@ion/core/src/utils/DateUtil';
import useMedia from '../../hooks/useMedia';
import {dlsAlt} from '../../theme/theme';
import {DateSelectorValues, DLSTheme, LastFilteredDateProps} from '../../types/types';
import {MobileDateRangePicker} from '../date/MobileDateRangePicker';
import {StaticDateRangeTimePicker} from '../date/StaticDateRangeTimePicker';
import {RadioMapType} from '../menus/DateFilterMegaMenu';
import RadioButton from '../radio/RadioButton';
import {getMinDaysForAssets} from './FilterUtils';

export interface DateFilterProps {
  customDateMode: boolean;
  isNotificationFilter?: boolean;
  onRadioChange?: (event: ChangeEvent<HTMLInputElement>, value: string) => void;
  selectedRadio?: DateSelectorValues;
  setSelectedDate?: Function;
  radioMap: RadioMapType;
  setDateError?: Function;
  totalAssets?: number;
  lastFilteredDateProps?: LastFilteredDateProps;
  overrideDateTimePickerStyles?: GenericObject;
  dateTimePickerPlaceHolder?: string;
}

interface StyleProps {
  customDateMode?: boolean;
  isNotificationFilter?: boolean;
}

const toggleDateModeStyles = (style1: string, style2: string) =>
  ({customDateMode}: StyleProps) => customDateMode ? style1 : style2
;

const useStyles = makeStyles((theme: DLSTheme) => ({
  selectionItem : {
    width                          : '50%',
    display                        : 'grid',
    height                         : 'fit-content',
    overflow                       : 'hidden',
    transition                     : 'transform 400ms ease-in-out',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px'
    }
  },
  dateSelection : {
    overflow                       : 'hidden',
    width                          : '100%',
    maxHeight                      : ({customDateMode, isNotificationFilter}: StyleProps) => customDateMode ? '400px' : isNotificationFilter ? '240px': '94px',
    margin                         : ({customDateMode}: StyleProps) => customDateMode ? '0px' : '30px 0px',
    transition                     : 'max-height 300ms ease-in-out',
    [theme.breakpoints.down('sm')] : {
      maxHeight : toggleDateModeStyles('100%', '230px')
    },
    [theme.breakpoints.down(620)] : {
      maxHeight : toggleDateModeStyles('100%', '275px')
    }
  },
  dateSelectionWrapper : {
    width                          : '200%',
    display                        : 'inline-flex',
    position                       : 'relative',
    [theme.breakpoints.down('sm')] : {
      width : '200%'
    }
  },
  radioSelectors : {
    transform : ({customDateMode}: StyleProps) =>
      `translateX(${!customDateMode ? '0%' : '-100%'})`
  },
  radioGroup : {
    display                        : 'grid',
    gridTemplateColumns            : 'repeat(3, 1fr)',
    gridGap                        : '3px',
    float                          : 'left',
    justifyContent                 : 'space-between',
    flexDirection                  : ({isNotificationFilter}: StyleProps) => isNotificationFilter ? 'column' : 'row',
    padding                        : '0 12px',
    [theme.breakpoints.down('sm')] : {
      display       : 'flex',
      alignItems    : 'flex-start',
      flexDirection : ({isNotificationFilter}: StyleProps) => isNotificationFilter ? 'column' : 'row'
    },
    [theme.breakpoints.down(620)] : {
      display       : 'flex',
      alignItems    : 'flex-start',
      flexDirection : ({isNotificationFilter}: StyleProps) => isNotificationFilter ? 'column' : 'row'
    }
  },
  radioItem : {
    padding    : '0px 12px',
    display    : 'flex',
    alignItems : 'center',
    '&:hover'  : {
      backgroundColor : theme.dls.alt.components.icon.selected
    }
  },
  radioIcon : {
    display    : 'flex',
    transition : 'transform 250ms ease-in-out',
    '&:hover'  : {
      cursor     : 'pointer',
      transform  : 'translateX(3px)',
      transition : 'transform 250ms ease-in-out'
    }
  },
  customDate : {
    transform : ({customDateMode}: StyleProps) =>
      `translateX(${!customDateMode ? '100%' : '-100%'})`,
    [theme.breakpoints.down('sm')] : {
      transform : ({customDateMode}: StyleProps) =>
        `translateX(${!customDateMode ? '100%' : '-100%'})`
    }
  },
  mobileRange : {
    width  : '85%',
    margin : '0 auto'
  },
  maxDays : {
    ...dlsAlt('typography', 'label3'),
    margin : '8px 0px'
  }
}));

/**
 * @DateFilter
 *
 * Date Filter Menu that utilizes the FilterMegaMenu, specifically to choose
 * a date or date range and pass those values back to the component consumer
 * user clicks done. Note that mobile content is just that, content, so needs
 * to be wrapped with some sort of drawer to be displayed properly. Done this
 * way to allow the content to be nested and abstract it from being forced to the
 * root of a drawer.
 *
 * ```typescript
 *
 * import {DateFilter} from '@ion/dls/src/components/menus';
 *
 * <DateFilter customDateMode={customDateMode}
 *   onRadioChange={onRadioChange}
 *   selectedRadio={selectedRadio}
 *   setSelectedDate={setSelectedDate}
 *   radioMap={radioMap}
 *   setDateError={setDateError} />
 *
 * ```
 *
 * @param {boolean} customDateMode: determines if the radios or date range picker
 *        should be shown
 * @param {Function} onRadioChange: callback fired when user selects new radio option
 * @param {DateSelectorValues} selectedRadio: presently selected radio option
 * @param {Function} setSelectedDate: sets the selected date range from the date
 *        range picker
 * @param {RadioMapType} radioMap: map that is looped over and rendered a radio
 *        button for each one
 * @param {Function} setDateError: function called when there is an error on the
 *        date range picker
 *
 */
export const DateFilter = ({
  customDateMode,
  isNotificationFilter = false,
  onRadioChange,
  selectedRadio,
  setSelectedDate,
  radioMap,
  lastFilteredDateProps,
  setDateError,
  totalAssets = 1,
  overrideDateTimePickerStyles = null,
  dateTimePickerPlaceHolder = null
}: DateFilterProps) => {
  const classes         = useStyles({customDateMode, isNotificationFilter});
  const {isPhone}       = useMedia();
  const maxHVSAssetDays = get('maxHVSAssetDays') ?? 30;
  const minDate = getMinDate(getMinDaysForAssets(maxHVSAssetDays, totalAssets));

  // if we don't get any value from maxHVSAssetDays then we default the limit to 30
  const shouldDisplay = (daysFromToday) => {
    return minDate <= getMinDate(daysFromToday);
  };

  function setError(val) {
    setDateError(val);
  }

  return (
    <div className={classes.dateSelection}>
      <div className={classes.dateSelectionWrapper}>
        <div className={classnames(classes.radioSelectors, classes.selectionItem)}>
          <RadioGroup className={classes.radioGroup}
            onChange={onRadioChange}
            value={selectedRadio}>
            <>
              {
                Object.entries(radioMap).map(([key, {label, daysFromToday, icon, onClick}], index) => {
                  return (
                    shouldDisplay(daysFromToday) ? (
                      <div key={`radio-option-${index}`}
                        className={classes.radioItem}
                        onClick={onClick}>
                        <RadioButton value={key}
                          label={label} />
                        {icon && (
                          <div className={classes.radioIcon}>
                            {icon}
                          </div>
                        )}
                      </div>
                    ) : null
                  );
                })
              }
            </>
          </RadioGroup>
        </div>
        <div className={classnames(classes.customDate, classes.selectionItem)}>
          <div className={classes.maxDays}>{translate('common:time.maxDays', {maxDays: maxHVSAssetDays})}</div>
          {
            isPhone ? (
              <div className={classes.mobileRange}>
                <MobileDateRangePicker disableFuture
                  setSelectedDate={setSelectedDate}
                  setDateError={setError}
                  lastFilteredDateProps={lastFilteredDateProps}
                  maxHVSAssetDays={maxHVSAssetDays}
                  overrideDateTimePickerStyles={overrideDateTimePickerStyles}
                  dateTimePickerPlaceHolder={dateTimePickerPlaceHolder} />
              </div>
            ) : (
              <StaticDateRangeTimePicker disableFuture
                setDateError={setDateError}
                lastFilteredDateProps={lastFilteredDateProps}
                inputFormat='MM/DD/YYYY hh:mm A'
                onRangeChange={function(dateRange) {
                  setSelectedDate(dateRange);
                }}
                maxHVSAssetDays={maxHVSAssetDays} />
            )
          }
        </div>
      </div>
    </div>
  );
};

export default DateFilter;
